import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useStoreScope } from "@/hooks/use-store-context";
import { api } from "../runtime";
import type { ApiKey, LeadImport, LeadImportFields, LeadImportRow, LeadImportStatus, LeadRowStatus, Page } from "../types";
import { qk } from "./keys";

const ACTIVE: LeadImportStatus[] = ["QUEUED", "PROCESSING"];
export const isImportActive = (i: Pick<LeadImport, "status">) => ACTIVE.includes(i.status);
/** Progress also arrives over SSE; polling covers a dropped connection. */
const POLL_MS = 3000;

export function useLeadImportFields() {
  return useQuery({
    queryKey: qk.leadImports.fields(),
    queryFn: () => api.get<LeadImportFields>("/v1/lead-imports/fields"),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export interface LeadImportFilters {
  store?: string;
  status?: LeadImportStatus[];
  kind?: "FILE" | "API";
}

export function useLeadImports(filters: LeadImportFilters = {}) {
  const scope = useStoreScope();
  return useInfiniteQuery({
    queryKey: qk.leadImports.list(scope, filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<LeadImport>>("/v1/lead-imports", {
        query: { storeId: filters.store, status: filters.status, kind: filters.kind, cursor: pageParam ?? undefined, limit: 25 },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some(isImportActive)) ? POLL_MS : false),
  });
}

export function useLeadImport(id: string) {
  return useQuery({
    queryKey: qk.leadImports.detail(id),
    queryFn: async () => (await api.get<{ import: LeadImport }>(`/v1/lead-imports/${id}`)).import,
    refetchInterval: (q) => (q.state.data && isImportActive(q.state.data) ? POLL_MS : false),
  });
}

export function useLeadImportRows(id: string, status: LeadRowStatus[] | undefined, live: boolean) {
  return useInfiniteQuery({
    queryKey: qk.leadImports.rows(id, { status }),
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<LeadImportRow>>(`/v1/lead-imports/${id}/rows`, { query: { status, cursor: pageParam ?? undefined, limit: 100 }, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: live ? POLL_MS : false,
  });
}

export function useApiKeys(enabled = true) {
  const scope = useStoreScope();
  return useQuery({
    queryKey: qk.apiKeys(scope),
    queryFn: async () => (await api.get<{ items: ApiKey[] }>("/v1/api-keys")).items,
    enabled,
  });
}
